/**
 * @flaha/shared-types — Organization, Membership, and Auth DTOs
 * (Phase 9A).
 *
 * Aligned 1:1 with the Phase 9A additions in `prisma/v2-schema.prisma`
 * (Organization, OrganizationMembership, RefreshToken). The auth DTOs
 * (AuthSessionDTO, RegisterRequest, LoginRequest, AuthMeResponse) live
 * in this file rather than `users.ts` so the Phase 8B session shapes
 * stay untouched during the migration window.
 */

import type { IsoDateString } from "./soil-domain";
import type { UserDTO } from "./users";

// ---------------------------------------------------------------------------
// Enums — mirror prisma/v2-schema.prisma
// ---------------------------------------------------------------------------

export enum OrganizationType {
	COMPANY = "COMPANY",
	CONSULTANCY = "CONSULTANCY",
	LABORATORY = "LABORATORY",
	FARM = "FARM",
	CLIENT = "CLIENT",
}

export enum OrganizationStatus {
	ACTIVE = "ACTIVE",
	SUSPENDED = "SUSPENDED",
	ARCHIVED = "ARCHIVED",
}

export enum OrganizationRole {
	OWNER = "OWNER",
	ADMIN = "ADMIN",
	AGRONOMIST = "AGRONOMIST",
	LAB_TECHNICIAN = "LAB_TECHNICIAN",
	CONSULTANT = "CONSULTANT",
	VIEWER = "VIEWER",
}

export enum MembershipStatus {
	PENDING = "PENDING",
	ACTIVE = "ACTIVE",
	SUSPENDED = "SUSPENDED",
	REMOVED = "REMOVED",
}

export enum InvitationStatus {
	PENDING = "PENDING",
	ACCEPTED = "ACCEPTED",
	EXPIRED = "EXPIRED",
	REVOKED = "REVOKED",
}

// ---------------------------------------------------------------------------
// Read DTOs
// ---------------------------------------------------------------------------

export interface OrganizationDTO {
	id: string;
	name: string;
	slug: string;
	type: OrganizationType;
	status: OrganizationStatus;
	createdAt: IsoDateString;
	updatedAt: IsoDateString;
}

export interface OrganizationMembershipDTO {
	id: string;
	organizationId: string;
	userId: string;
	role: OrganizationRole;
	status: MembershipStatus;
	invitedById: string | null;
	invitedAt: IsoDateString | null;
	acceptedAt: IsoDateString | null;
	createdAt: IsoDateString;
	updatedAt: IsoDateString;
	/** Hydrated when the endpoint returns the parent org alongside. */
	organization?: OrganizationDTO;
}

// ---------------------------------------------------------------------------
// Auth — request DTOs
// ---------------------------------------------------------------------------

export interface RegisterRequest {
	email: string;
	password: string;
	displayName: string;
	/** Optional personal org name; falls back to "<displayName>'s workspace". */
	organizationName?: string;
}

export interface LoginRequest {
	email: string;
	password: string;
}

/** Phase 11-D — change password while authenticated. */
export interface ChangePasswordRequest {
	currentPassword: string;
	newPassword: string;
}

export interface ChangePasswordResponse {
	ok: true;
}

/** Phase 11-D — transfer OWNER seat (UI must show confirm step). */
export interface TransferOwnershipRequest {
	targetUserId: string;
	/** Must be the literal string "TRANSFER". */
	confirmation: "TRANSFER";
}

export interface TransferOwnershipResponse {
	ok: true;
	previousOwnerId: string;
	newOwnerId: string;
}

export interface LeaveOrganizationResponse {
	ok: true;
}

export interface SuspendMembershipResponse {
	member: OrganizationMemberDTO;
}

// ---------------------------------------------------------------------------
// Auth — response DTOs
//
// Refresh tokens are NEVER returned in the JSON body — they live in the
// HttpOnly+Secure `fsoil_rt` cookie. The shapes below describe only the
// access token + session context the client actually needs to know
// about.
// ---------------------------------------------------------------------------

export interface AuthSessionDTO {
	accessToken: string;
	/** Absolute expiry of the access token. Client uses this to
	 * proactively refresh ~1 minute before expiry. */
	accessTokenExpiresAt: IsoDateString;
	user: UserDTO;
	activeOrganization: OrganizationDTO | null;
	memberships: OrganizationMembershipDTO[];
}

export interface AuthLoginResponse {
	session: AuthSessionDTO;
}

export interface AuthRegisterResponse {
	session: AuthSessionDTO;
}

export interface AuthRefreshResponse {
	session: AuthSessionDTO;
}

export interface AuthLogoutResponse {
	ok: true;
}

export interface AuthMeResponse {
	user: UserDTO;
	activeOrganization: OrganizationDTO | null;
	memberships: OrganizationMembershipDTO[];
}

// ---------------------------------------------------------------------------
// Phase 9A-H — Organization management & switching
//
// `GET /api/v2/me/organizations` lists the caller's ACTIVE memberships
// with hydrated organizations and surfaces the active org id so the
// frontend tenant switcher can highlight it without a second call.
//
// `POST /api/v2/auth/switch-organization` rotates the access token by
// re-issuing a JWT with the new `oid` claim. The response shape mirrors
// the login/refresh flow so the frontend can reuse its existing
// `applySession` reducer.
// ---------------------------------------------------------------------------

export interface UserMembershipsResponse {
	activeOrganizationId: string | null;
	memberships: OrganizationMembershipDTO[];
}

export interface SwitchOrganizationRequest {
	organizationId: string;
}

export type SwitchOrganizationResponse = AuthRefreshResponse;

// ---------------------------------------------------------------------------
// Email verification + password recovery
// ---------------------------------------------------------------------------

export interface VerifyEmailRequest {
	token: string;
}

export interface VerifyEmailResponse {
	ok: true;
	user: import("./users").UserDTO;
}

export interface ResendVerificationResponse {
	ok: true;
}

export interface ForgotPasswordRequest {
	email: string;
}

/** Always 200 with generic ok — never leak account existence. */
export interface ForgotPasswordResponse {
	ok: true;
}

export interface ResetPasswordRequest {
	token: string;
	password: string;
}

export interface ResetPasswordResponse {
	ok: true;
}

// ---------------------------------------------------------------------------
// Phase 9B — Organization administration
//
// Endpoints (all under `/api/v2/organizations/:organizationId/*` unless
// noted, gated by `requireOrganizationAdmin` which returns 404 when the
// caller has no membership in the target org so existence does not leak):
//
//   GET  /organizations/:organizationId                — any active member
//   PATCH /organizations/:organizationId               — OWNER/ADMIN
//   GET  /organizations/:organizationId/members        — any active member
//   PATCH /organizations/:organizationId/members/:userId   — OWNER/ADMIN
//   DELETE /organizations/:organizationId/members/:userId  — OWNER/ADMIN
//   GET  /organizations/:organizationId/invitations    — OWNER/ADMIN
//   POST /organizations/:organizationId/invitations    — OWNER/ADMIN
//   DELETE /organizations/:organizationId/invitations/:invitationId
//                                                     — OWNER/ADMIN
//   POST /invitations/accept                           — any authed user
// ---------------------------------------------------------------------------

export interface OrganizationInvitationDTO {
	id: string;
	organizationId: string;
	email: string;
	role: OrganizationRole;
	status: InvitationStatus;
	invitedByUserId: string;
	expiresAt: IsoDateString;
	acceptedAt: IsoDateString | null;
	revokedAt: IsoDateString | null;
	createdAt: IsoDateString;
	updatedAt: IsoDateString;
}

/**
 * Membership row hydrated with the user's display info for the org
 * Members page. `userEmail` and `userDisplayName` come from a JOIN on
 * `users`; sensitive fields (passwordHash, refresh tokens, audit rows)
 * are never included.
 */
export interface OrganizationMemberDTO extends OrganizationMembershipDTO {
	userEmail: string;
	userDisplayName: string;
}

// --- GET / PATCH /organizations/:organizationId ----------------------------

export interface GetOrganizationResponse {
	organization: OrganizationDTO;
}

export interface PatchOrganizationRequest {
	name?: string;
	type?: OrganizationType;
}

export interface PatchOrganizationResponse {
	organization: OrganizationDTO;
}

// --- Members ---------------------------------------------------------------

export interface ListOrganizationMembersResponse {
	members: OrganizationMemberDTO[];
}

export interface PatchMembershipRequest {
	role: OrganizationRole;
}

export interface PatchMembershipResponse {
	member: OrganizationMemberDTO;
}

export interface RemoveMembershipResponse {
	ok: true;
}

// --- Invitations -----------------------------------------------------------

export interface CreateInvitationRequest {
	email: string;
	role: OrganizationRole;
}

export interface CreateInvitationResponse {
	invitation: OrganizationInvitationDTO;
}

export interface ListInvitationsResponse {
	invitations: OrganizationInvitationDTO[];
}

export interface RevokeInvitationResponse {
	invitation: OrganizationInvitationDTO;
}

export interface AcceptInvitationRequest {
	token: string;
}

export interface AcceptInvitationResponse {
	membership: OrganizationMembershipDTO;
	organization: OrganizationDTO;
}
